import type { Ref } from 'vue'
import { Z_INDEX } from '@/constants/zIndex'

/**
 * Z-index used by the verdict feature hint floater above the chat composer.
 *
 * Must stay above the Radix popover ``PopoverContent`` default so the hint continues
 * to float over it, while remaining below app-chrome layers such as dialog overlays.
 * Re-exported from :mod:`@/constants/zIndex` to keep chat-composer call sites
 * friction-free while the raw value lives in the shared layering module.
 */
export const VERDICT_HINT_Z_INDEX = Z_INDEX.VERDICT_HINT

/**
 * Bindings that guard a popover against dismissal while the verdict feature hint is
 * still visible. The user must acknowledge the hint (via ``dismiss``) before the
 * popover is allowed to close.
 *
 * Pointer / focus guards keep the popover open on outside clicks or focus shifts,
 * while the escape handler *does* route to ``dismiss`` so keyboard users can still
 * clear both the hint and the popover (WCAG 2.1 SC 1.4.13 dismissible non-modal
 * overlay requirement).
 *
 * :param showVerdictFeatureHint: Reactive flag indicating the hint is shown.
 * :param dismiss: Callback invoked when the user presses Escape while the hint is
 *     visible; expected to hide the hint and close the popover.
 *
 * :return: Handlers to bind directly to ``<PopoverContent>`` v-events, plus the
 *     shared z-index constant for the hint container.
 */
export function useVerdictHintPopoverGuard(
  showVerdictFeatureHint: Ref<boolean>,
  dismiss: () => void | Promise<void>
) {
  const preventPopoverDismissWhileHint = (event: Event) => {
    if (showVerdictFeatureHint.value) {
      event.preventDefault()
    }
  }

  const dismissVerdictHintOnEscape = (event: Event) => {
    if (!showVerdictFeatureHint.value) return
    event.preventDefault()
    void dismiss()
  }

  return {
    preventPopoverDismissWhileHint,
    dismissVerdictHintOnEscape,
    verdictHintZIndex: VERDICT_HINT_Z_INDEX
  }
}
